import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Linkedin, ArrowUpRight, Check, Loader2 } from "lucide-react";
import useSEO from "@/lib/useSEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { CONTACT, CONFIDENTIALITY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-white border border-border-subtle px-4 py-3 text-charcoal text-[0.95rem] placeholder:text-charcoal/40 focus:border-navy transition-colors duration-300 outline-none";
const labelCls = "block text-xs font-semibold tracking-wider uppercase text-charcoal/70 mb-2";

const Contact = () => {
  useSEO({
    title: "Contact",
    description:
      "Contact Arun Gupta & Co. Chartered Accountants in Kanpur. Office at Roland Complex, The Mall, Kanpur - 208001. Phone +91 9336117987 · ag.arunguptaco@gmail.com.",
    path: "/contact",
  });

  const [form, setForm] = useState({ full_name: "", organisation: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) return toast.error("Please enter your full name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Please enter a valid email address.");
    if (form.message.trim().length < 5) return toast.error("Please share a little about your requirement.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you. Your message has been received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        titleLines={["Have a requirement", "you'd like to discuss?"]}
        intro="Whether you need support with an existing requirement or would like to discuss how we may be able to assist your business, we'd be glad to hear from you."
        testId="contact-hero"
      />

      <section className="py-16 lg:py-24" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Details */}
          <div className="lg:col-span-4">
            <Reveal>
              <div className="overline">The Firm</div>
              <h2 className="font-serif text-navy text-3xl lg:text-4xl mt-4 leading-tight">Arun Gupta &amp; Co.</h2>
              <div className="overline mt-2">Chartered Accountants</div>

              <div className="mt-10 space-y-8">
                <div className="flex gap-4">
                  <MapPin className="text-brass shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <address className="not-italic text-[0.95rem] leading-relaxed text-charcoal/80">
                    {CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}
                  </address>
                </div>
                <div className="flex gap-4">
                  <Phone className="text-brass shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <div className="text-[0.95rem] text-charcoal/80 space-y-1">
                    {CONTACT.phones.map((p) => (
                      <a key={p} href={`tel:${p.replace(/\s+/g, "")}`} className="block link-underline w-fit" data-testid={`contact-phone-${p.replace(/\D/g, "")}`}>{p}</a>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4">
                  <Mail className="text-brass shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <a href={`mailto:${CONTACT.email}`} className="text-[0.95rem] text-charcoal/80 link-underline w-fit break-all" data-testid="contact-email-link">{CONTACT.email}</a>
                </div>
                <div className="flex gap-4">
                  <Linkedin className="text-brass shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 whitespace-nowrap text-[0.95rem] text-charcoal/80 link-underline w-fit" data-testid="contact-linkedin-link">
                    Company LinkedIn <ArrowUpRight size={14} strokeWidth={1.5} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 lg:col-start-6">
            {done ? (
              <div className="border border-border-subtle bg-white p-10 lg:p-14 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full border border-brass flex items-center justify-center mx-auto">
                  <Check className="text-brass" size={26} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-navy text-3xl mt-6">Message received.</h3>
                <p className="mt-4 text-charcoal/75 max-w-md mx-auto">
                  Thank you for reaching out. We will review your message and respond as soon as we
                  are able.
                </p>
              </div>
            ) : (
              <Reveal>
                <form onSubmit={submit} className="border border-border-subtle bg-white p-6 sm:p-10" data-testid="contact-form" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls} htmlFor="name">Full Name</label>
                      <input id="name" name="full_name" value={form.full_name} onChange={onChange} className={field} placeholder="Your name" data-testid="contact-name-input" required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="org">Organisation</label>
                      <input id="org" name="organisation" value={form.organisation} onChange={onChange} className={field} placeholder="Company / firm (optional)" data-testid="contact-org-input" />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="email">Email</label>
                      <input id="email" name="email" type="email" value={form.email} onChange={onChange} className={field} placeholder="you@example.com" data-testid="contact-email-input" required />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="phone">Phone</label>
                      <input id="phone" name="phone" value={form.phone} onChange={onChange} className={field} placeholder="+91" data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <div className="mt-5">
                    <label className={labelCls} htmlFor="message">Requirement / Message</label>
                    <textarea id="message" name="message" value={form.message} onChange={onChange} rows={5} className={field} placeholder="Briefly describe what you'd like to discuss" data-testid="contact-message-input" required />
                  </div>
                  <p className="mt-5 text-xs leading-relaxed text-charcoal/55">{CONFIDENTIALITY}</p>
                  <button type="submit" className="btn-solid mt-7 w-full sm:w-auto justify-center" disabled={submitting} data-testid="contact-submit-btn">
                    {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Message</>}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 lg:pb-28" data-testid="contact-map-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex items-center justify-between mb-6">
              <div className="overline">Find Us</div>
              <a href={CONTACT.maps} target="_blank" rel="noopener noreferrer" className="arrow-link" data-testid="contact-directions-link">
                <span className="link-underline">Open in Google Maps</span>
                <ArrowUpRight size={15} strokeWidth={1.5} />
              </a>
            </div>
            <div className="relative overflow-hidden border border-border-subtle" style={{ aspectRatio: "16 / 7", background: "#E7E2D8" }}>
              <iframe
                title="Arun Gupta & Co. office location on Google Maps"
                src={CONTACT.mapsEmbed}
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="contact-map-iframe"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
